
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { DataTable } from "@/components/ui/data-table";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { Download, TrendingUp, DollarSign, Users, CreditCard } from "lucide-react";

interface FinancialAnalyticsProps {
  programId?: string;
}

export function FinancialAnalytics({ programId }: FinancialAnalyticsProps) {
  const [selectedMerchant, setSelectedMerchant] = useState<string>("");
  const [reportType, setReportType] = useState<string>("");
  const [dateRange, setDateRange] = useState<any>(null);

  // Mock data for financial analytics
  const revenueSummary = {
    totalRevenue: 2485000,
    monthlyGrowth: 12.5,
    activeMembers: 15420,
    avgRevenuePerMember: 161.2
  };

  const monthlyRevenueData = [
    { month: 'Jan', revenue: 385000, transactions: 2240, members: 14250 },
    { month: 'Feb', revenue: 425000, transactions: 2480, members: 14680 },
    { month: 'Mar', revenue: 395000, transactions: 2380, members: 14920 },
    { month: 'Apr', revenue: 465000, transactions: 2720, members: 15180 },
    { month: 'May', revenue: 485000, transactions: 2850, members: 15420 },
    { month: 'Jun', revenue: 525000, transactions: 3120, members: 15680 }
  ];

  const merchantPerformanceData = [
    { 
      name: 'Carrefour', 
      revenue: 785000, 
      transactions: 4200, 
      commission: 23550,
      growth: 15.2,
      category: 'Retail',
      avgTicket: 187
    },
    { 
      name: 'Mall of Emirates', 
      revenue: 598000, 
      transactions: 2100, 
      commission: 17940,
      growth: 8.7,
      category: 'Shopping',
      avgTicket: 285
    },
    { 
      name: 'Starbucks', 
      revenue: 387000, 
      transactions: 3800, 
      commission: 11610,
      growth: 22.1,
      category: 'F&B',
      avgTicket: 102
    },
    { 
      name: 'Vox Cinemas', 
      revenue: 356000, 
      transactions: 1200, 
      commission: 10680,
      growth: 5.4,
      category: 'Entertainment',
      avgTicket: 297
    },
    { 
      name: 'Adidas', 
      revenue: 334000, 
      transactions: 900, 
      commission: 10020,
      growth: 18.9,
      category: 'Retail',
      avgTicket: 371
    }
  ];

  const revenueByCategory = [
    { name: 'Retail', value: 45, revenue: 1119000 },
    { name: 'F&B', value: 28, revenue: 695600 },
    { name: 'Entertainment', value: 17, revenue: 422450 },
    { name: 'Travel', value: 6, revenue: 149100 },
    { name: 'Services', value: 4, revenue: 99400 }
  ];

  const chartConfig = {
    revenue: { label: "Revenue", color: "#10b981" },
    transactions: { label: "Transactions", color: "#3b82f6" },
    members: { label: "Members", color: "#8b5cf6" },
    commission: { label: "Commission", color: "#f59e0b" },
    growth: { label: "Growth %", color: "#ef4444" }
  };

  const merchantColumns = [
    {
      header: "Merchant",
      accessorKey: "name" as keyof typeof merchantPerformanceData[0]
    },
    {
      header: "Revenue",
      cell: (row: typeof merchantPerformanceData[0]) => `$${row.revenue.toLocaleString()}`
    },
    {
      header: "Transactions",
      cell: (row: typeof merchantPerformanceData[0]) => row.transactions.toLocaleString()
    },
    {
      header: "Avg Ticket",
      cell: (row: typeof merchantPerformanceData[0]) => `$${row.avgTicket}`
    },
    {
      header: "Commission",
      cell: (row: typeof merchantPerformanceData[0]) => `$${row.commission.toLocaleString()}`
    },
    {
      header: "Growth",
      cell: (row: typeof merchantPerformanceData[0]) => (
        <span className={`flex items-center ${row.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          <TrendingUp className="h-4 w-4 mr-1" />
          {row.growth}%
        </span>
      )
    },
    {
      header: "Category",
      accessorKey: "category" as keyof typeof merchantPerformanceData[0]
    }
  ];

  const generateCustomReport = () => {
    console.log('Generating custom report with:', {
      reportType,
      selectedMerchant,
      dateRange
    });
    // Implementation for custom report generation
  };

  const exportData = (format: string) => {
    console.log(`Exporting data in ${format} format`);
    // Implementation for data export
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="summary" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="summary">Revenue Summary</TabsTrigger>
          <TabsTrigger value="merchants">Merchant Drill-down</TabsTrigger>
          <TabsTrigger value="reports">Custom Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="space-y-6">
          {/* Revenue KPIs */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${revenueSummary.totalRevenue.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">
                  +{revenueSummary.monthlyGrowth}% from last month
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Members</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{revenueSummary.activeMembers.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">
                  Currently enrolled
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Revenue/Member</CardTitle>
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${revenueSummary.avgRevenuePerMember}</div>
                <p className="text-xs text-muted-foreground">
                  Per active member
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Growth Rate</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">+{revenueSummary.monthlyGrowth}%</div>
                <p className="text-xs text-muted-foreground">
                  Monthly growth
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Revenue Trends Chart */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Monthly Revenue Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px]">
                  <LineChart data={monthlyRevenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line type="monotone" dataKey="revenue" stroke="var(--color-revenue)" strokeWidth={2} />
                  </LineChart>
                </ChartContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Revenue by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px]">
                  <PieChart>
                    <Pie
                      data={revenueByCategory}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}%`}
                    >
                      {revenueByCategory.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={`hsl(${index * 72}, 70%, 50%)`} />
                      ))}
                    </Pie>
                    <ChartTooltip content={<ChartTooltipContent />} />
                  </PieChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="merchants" className="space-y-6">
          {/* Merchant Performance Overview */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>Top Revenue Generator</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{merchantPerformanceData[0].name}</div>
                <p className="text-lg text-green-600">${merchantPerformanceData[0].revenue.toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">
                  {merchantPerformanceData[0].transactions.toLocaleString()} transactions
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Fastest Growing</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">Starbucks</div>
                <p className="text-lg text-green-600">+22.1%</p>
                <p className="text-sm text-muted-foreground">Monthly growth rate</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Highest Avg Ticket</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">Adidas</div>
                <p className="text-lg text-blue-600">$371</p>
                <p className="text-sm text-muted-foreground">Average transaction</p>
              </CardContent>
            </Card>
          </div>

          {/* Merchant Performance Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Merchant Revenue Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[300px]">
                <BarChart data={merchantPerformanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="revenue" fill="var(--color-revenue)" />
                </BarChart>
              </ChartContainer>
            </CardContent>
          </Card>

          {/* Detailed Merchant Table */}
          <DataTable
            title="Merchant Performance Details"
            data={merchantPerformanceData}
            columns={merchantColumns}
          />
        </TabsContent>

        <TabsContent value="reports" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Custom Report Generator</CardTitle>
              <p className="text-sm text-muted-foreground">
                Create custom financial reports with specific parameters and filters
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="report-type">Report Type</Label>
                  <Select value={reportType} onValueChange={setReportType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select report type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="revenue-analysis">Revenue Analysis</SelectItem>
                      <SelectItem value="merchant-performance">Merchant Performance</SelectItem>
                      <SelectItem value="category-breakdown">Category Breakdown</SelectItem>
                      <SelectItem value="commission-report">Commission Report</SelectItem>
                      <SelectItem value="growth-analysis">Growth Analysis</SelectItem>
                      <SelectItem value="member-value">Member Value Analysis</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="merchant-filter">Merchant Filter</Label>
                  <Select value={selectedMerchant} onValueChange={setSelectedMerchant}>
                    <SelectTrigger>
                      <SelectValue placeholder="All merchants" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Merchants</SelectItem>
                      {merchantPerformanceData.map((merchant) => (
                        <SelectItem key={merchant.name} value={merchant.name}>
                          {merchant.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date Range</Label>
                  <DatePickerWithRange 
                    date={dateRange} 
                    setDate={setDateRange}
                    placeholder="Select date range"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={generateCustomReport} disabled={!reportType}>
                  Generate Report
                </Button>
                <Button variant="outline" onClick={() => exportData('pdf')}>
                  <Download className="h-4 w-4 mr-2" />
                  Export PDF
                </Button>
                <Button variant="outline" onClick={() => exportData('excel')}>
                  <Download className="h-4 w-4 mr-2" />
                  Export Excel
                </Button>
                <Button variant="outline" onClick={() => exportData('csv')}>
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Report Preview */}
          {reportType && (
            <Card>
              <CardHeader>
                <CardTitle>Report Preview</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Preview of {reportType.replace('-', ' ')} report
                </p>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-muted-foreground">
                  <p>Report preview will be generated based on selected parameters</p>
                  <p className="text-sm mt-2">
                    Report Type: {reportType} | 
                    Merchant: {selectedMerchant || 'All'} | 
                    Date Range: {dateRange ? 'Selected' : 'All time'}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
